import React from "react";
import { Approvals } from "./Approvals";
import { MemoPanel } from "./MemoPanel";
import { DocumentsPanel } from "./DocumentsPanel";
import { TrackersPanel, type TrackersMap } from "./TrackersPanel";
import { ActionsPanel } from "./ActionsPanel";
import { CheckToggles, type CheckToggleEntry } from "./CheckToggles";
import { UsagePanel } from "./UsagePanel";
import { EvalPanel } from "./EvalPanel";
import type {
  ActionLog, Approval, CostState, EvalResult, MemosMap,
} from "../../types";
import type { MemoPanelMatter } from "./MemoPanel";
import type { SAStatus } from "../../sa";

export interface RightColProps {
  rightTab: string;
  setRightTab: (id: string) => void;
  pendingCount: number;
  evalAttached: boolean;
  approvals: Approval[];
  decide: (key: string, ok: boolean, chosen?: string) => void;
  decideAll: (ok: boolean) => void;
  wsName: (id: string) => string;
  memos: MemosMap;
  activeWs: string;
  setActiveWs: (id: string) => void;
  wsDefs: MemoPanelMatter[];
  actionsByAgent: Record<string, ActionLog[]>;
  /** Latest real skip reason per agent check id (live runs). */
  agentSkips?: Record<string, string>;
  /** wsId → document register entries accumulated over the run. */
  documentRegister?: Record<string, any[]>;
  /** Accumulated per-workstream tracker state (live orchestrator runs). */
  trackers?: TrackersMap | null;
  trackerKeys?: Set<string>;
  /** Resolves an email id to the workstream it was routed to. */
  routedWs: (emailId: number) => string;
  reprocs: ActionLog[];
  activeAgents: Set<string>;
  toggleAgent: (id: string) => void;
  toggledOff: Set<string>;
  toggleCheck: (id: string) => void;
  thresholds: Record<string, number>;
  setThreshold: (id: string, v: number) => void;
  firmThresholds?: Record<string, number> | null;
  saveFirmThresholds?: () => Promise<boolean>;
  applyFirmThresholds?: () => void;
  saStatus?: SAStatus;
  checkList: CheckToggleEntry[];
  cost: CostState;
  evalResult: EvalResult;
  evalName: string;
  readOnly?: boolean;
}

const NOOP_SAVE = async (): Promise<boolean> => false;
const NOOP_VOID = (): void => {};

/* Right column tab switcher — flips between Approvals / Memo / Documents /
   Trackers / Actions / Checks / Usage / Eval depending on rightTab. */
export function RightCol(p: RightColProps): JSX.Element {
  const rtabsList: Array<[string, string]> = [
    ["approvals", "Approvals"],
    ["memo", "Memo"],
    ["docs", "Documents"],
    ["trackers", "Trackers"],
    ["actions", "Actions"],
    ["checks", "Checks"],
    ["cost", "Usage"],
  ];
  if (p.evalAttached) rtabsList.push(["eval", "Eval"]);
  return (
    <div className="scol">
      <div className="rtabs">
        {rtabsList.map(([id, lab]) => (
          <button
            key={id}
            className={p.rightTab === id ? "on" : ""}
            onClick={() => p.setRightTab(id)}
          >
            {lab}
            {id === "approvals" && p.pendingCount > 0 && (
              <span className="cbadge" style={{ color: "var(--red)" }}>
                {p.pendingCount}
              </span>
            )}
          </button>
        ))}
      </div>
      <div className="rbody scroll">
        {p.rightTab === "approvals" && (
          <Approvals
            approvals={p.approvals}
            decide={p.decide}
            decideAll={p.decideAll}
            wsName={p.wsName}
            readOnly={p.readOnly}
          />
        )}
        {p.rightTab === "memo" && (
          <MemoPanel
            memos={p.memos}
            activeWs={p.activeWs}
            setActiveWs={p.setActiveWs}
            wsDefs={p.wsDefs}
          />
        )}
        {p.rightTab === "docs" && <DocumentsPanel register={p.documentRegister || {}} />}
        {p.rightTab === "trackers" && (
          <TrackersPanel
            trackers={p.trackers}
            byAgent={p.actionsByAgent}
            routedWs={p.routedWs}
            activeWs={p.activeWs}
            setActiveWs={p.setActiveWs}
            wsDefs={p.wsDefs}
            activeAgents={p.activeAgents}
            trackerKeys={p.trackerKeys}
          />
        )}
        {p.rightTab === "actions" && (
          <ActionsPanel
            byAgent={p.actionsByAgent}
            agentSkips={p.agentSkips}
            reprocs={p.reprocs}
            activeAgents={p.activeAgents}
            toggleAgent={p.toggleAgent}
            readOnly={p.readOnly}
          />
        )}
        {p.rightTab === "checks" && (
          <CheckToggles
            toggledOff={p.toggledOff}
            toggleCheck={p.toggleCheck}
            thresholds={p.thresholds}
            setThreshold={p.setThreshold}
            firmThresholds={p.firmThresholds ?? null}
            saveFirmThresholds={p.saveFirmThresholds ?? NOOP_SAVE}
            applyFirmThresholds={p.applyFirmThresholds ?? NOOP_VOID}
            saStatus={p.saStatus ?? "offline"}
            checks={p.checkList}
            readOnly={p.readOnly}
            cost={p.cost}
          />
        )}
        {p.rightTab === "cost" && <UsagePanel cost={p.cost} approvals={p.approvals} />}
        {p.rightTab === "eval" && <EvalPanel ev={p.evalResult} evalName={p.evalName} />}
      </div>
    </div>
  );
}
